import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Check, AlertCircle, Zap, FileText, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsTab = ({ user }) => {
  const [digitalSignature, setDigitalSignature] = useState(null);
  const [prescriptionBackground, setPrescriptionBackground] = useState(null);
  const [quickAppointmentEnabled, setQuickAppointmentEnabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchDoctorData();
      fetchDoctorSettings();
    }
  }, [user?.id]);

  const fetchDoctorData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/doctors/${user.id}`);
      setDigitalSignature(response.data.digital_signature_url || null);
      setPrescriptionBackground(response.data.prescription_background_pdf || null);
    } catch (error) {
      console.error('Error fetching doctor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorSettings = async () => {
    try {
      const response = await axios.get(`${API}/doctor-settings/${user.id}`);
      setQuickAppointmentEnabled(response.data.quick_appointment_enabled || false);
    } catch (error) {
      console.error('Error fetching doctor settings:', error);
    }
  };

  const handleQuickAppointmentToggle = async (checked) => {
    try {
      await axios.put(`${API}/doctor-settings/${user.id}?quick_appointment_enabled=${checked}`);
      setQuickAppointmentEnabled(checked);
      toast.success(checked ? 'Quick Appointment enabled' : 'Quick Appointment disabled');
    } catch (error) {
      console.error('Error updating quick appointment setting:', error);
      toast.error('Failed to update setting');
    }
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size should be less than 2MB');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Upload file
      const uploadResponse = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const signatureUrl = uploadResponse.data.url;

      // Update doctor's digital signature
      await axios.patch(`${API}/doctors/${user.id}/digital-signature`, {
        digital_signature_url: signatureUrl,
      });

      setDigitalSignature(signatureUrl);
      toast.success('Digital signature uploaded successfully');
      
      // Reset file input
      document.getElementById('signature-upload').value = '';
    } catch (error) {
      console.error('Error uploading signature:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload digital signature';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveSignature = async () => {
    try {
      await axios.patch(`${API}/doctors/${user.id}/digital-signature`, {
        digital_signature_url: null,
      });

      setDigitalSignature(null);
      toast.success('Digital signature removed');
    } catch (error) {
      console.error('Error removing signature:', error);
      toast.error('Failed to remove digital signature');
    }
  };

  // Prescription Background PDF handlers
  const handleBackgroundPdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    setUploadingPdf(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Upload PDF file
      const uploadResponse = await axios.post(`${API}/upload-pdf`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const pdfUrl = uploadResponse.data.url;

      // Update doctor's prescription background
      await axios.patch(`${API}/doctors/${user.id}/prescription-background`, {
        prescription_background_pdf: pdfUrl,
      });

      setPrescriptionBackground(pdfUrl);
      toast.success('Prescription background uploaded successfully');
      
      // Reset file input
      document.getElementById('background-pdf-upload').value = '';
    } catch (error) {
      console.error('Error uploading prescription background:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload prescription background';
      toast.error(errorMsg);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleRemoveBackgroundPdf = async () => {
    try {
      await axios.patch(`${API}/doctors/${user.id}/prescription-background`, {
        prescription_background_pdf: null,
      });

      setPrescriptionBackground(null);
      toast.success('Prescription background removed');
    } catch (error) {
      console.error('Error removing prescription background:', error);
      toast.error('Failed to remove prescription background');
    }
  };

  const handlePreviewBackgroundPdf = () => {
    if (prescriptionBackground) {
      const fullUrl = prescriptionBackground.startsWith('http') 
        ? prescriptionBackground 
        : `${BACKEND_URL}${prescriptionBackground}`;
      window.open(fullUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-3xl">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Settings</h3>

      {/* Digital Signature Section */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium text-gray-900 mb-2 block">
            Digital Signature
          </Label>
          <p className="text-sm text-gray-600 mb-4">
            Upload your digital signature to be included in prescription PDFs. The signature will appear when patients download prescriptions, but not when printing for manual signing.
          </p>
        </div>

        {digitalSignature ? (
          <div className="space-y-4">
            {/* Preview */}
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-3">Current Digital Signature:</p>
              <div className="bg-white p-4 rounded border border-gray-200 inline-block">
                <img
                  src={digitalSignature.startsWith('http') ? digitalSignature : `${BACKEND_URL}${digitalSignature}`}
                  alt="Digital Signature"
                  className="max-h-24 max-w-full object-contain"
                  onError={(e) => {
                    console.error('Failed to load digital signature preview:', digitalSignature);
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="50"><text x="10" y="30" fill="red" font-size="12">Image failed to load</text></svg>';
                  }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => document.getElementById('signature-upload').click()}
                variant="outline"
                disabled={uploading}
              >
                <Upload className="w-4 h-4 mr-2" />
                Replace Signature
              </Button>
              <Button
                onClick={handleRemoveSignature}
                variant="destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Signature
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">No digital signature uploaded</p>
            <Button
              onClick={() => document.getElementById('signature-upload').click()}
              disabled={uploading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Signature
                </>
              )}
            </Button>
          </div>
        )}

        {/* Hidden file input */}
        <input
          id="signature-upload"
          type="file"
          accept="image/*"
          onChange={handleSignatureUpload}
          className="hidden"
        />

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Tips for best results:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Use a transparent background PNG for best results</li>
              <li>Signature should be clear and legible</li>
              <li>Recommended size: 300x100 pixels or similar aspect ratio</li>
              <li>Maximum file size: 2MB</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-8"></div>

      {/* Quick Appointment Section */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium text-gray-900 mb-2 block">
            Quick Appointment
          </Label>
          <p className="text-sm text-gray-600 mb-4">
            Enable this to show a "Quick Appointment" button on your dashboard. This allows you to quickly book appointments without going through the front desk.
          </p>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Enable Quick Appointment</p>
              <p className="text-sm text-gray-500">Book appointments directly from your dashboard</p>
            </div>
          </div>
          <Switch
            checked={quickAppointmentEnabled}
            onCheckedChange={handleQuickAppointmentToggle}
          />
        </div>

        {quickAppointmentEnabled && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-medium">Quick Appointment is enabled</p>
              <p className="text-green-700">You'll see a "Quick Appointment" button next to "Quick Lookup" on your dashboard.</p>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-8"></div>

      {/* Prescription Background Section */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium text-gray-900 mb-2 block">
            Prescription Background (Letterhead)
          </Label>
          <p className="text-sm text-gray-600 mb-4">
            Upload an A4 PDF page to use as the background for your prescriptions. When prescriptions are downloaded as PDF, they will be rendered on top of this background instead of a plain white page.
          </p>
        </div>

        {prescriptionBackground ? (
          <div className="space-y-4">
            {/* Preview */}
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-3">Current Prescription Background:</p>
              <div className="bg-white p-4 rounded border border-gray-200 flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Letterhead PDF</p>
                  <p className="text-sm text-gray-500">A4 Background uploaded</p>
                </div>
                <Button
                  onClick={handlePreviewBackgroundPdf}
                  variant="outline"
                  size="sm"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => document.getElementById('background-pdf-upload').click()}
                variant="outline"
                disabled={uploadingPdf}
              >
                <Upload className="w-4 h-4 mr-2" />
                Replace Background
              </Button>
              <Button
                onClick={handleRemoveBackgroundPdf}
                variant="destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Background
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">No prescription background uploaded</p>
            <Button
              onClick={() => document.getElementById('background-pdf-upload').click()}
              disabled={uploadingPdf}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploadingPdf ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Background PDF
                </>
              )}
            </Button>
          </div>
        )}

        {/* Hidden file input */}
        <input
          id="background-pdf-upload"
          type="file"
          accept="application/pdf"
          onChange={handleBackgroundPdfUpload}
          className="hidden"
        />

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Tips for best results:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Use an A4 size PDF (210mm × 297mm)</li>
              <li>Include your clinic/hospital letterhead design</li>
              <li>Leave space at the top for prescription content</li>
              <li>Maximum file size: 5MB</li>
              <li>The prescription content will overlay on this background when downloaded</li>
            </ul>
          </div>
        </div>

        {prescriptionBackground && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-medium">Background Active</p>
              <p className="text-green-700">Your prescriptions will be downloaded with this background instead of a plain white page.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsTab;
